// eslint-disable-next-line import/no-unresolved
import type { CardOptions, CardClassSelectors, ComponentPropertyValues, InputPropertyValues, InputTextPropertyValues, ComponentStatePropertyValues } from '@square/web-payments-sdk-types';
import type { PaymentFormStyle } from './payment-form-style';

// Alias this type since VS code seems to not find it for some reason, and they are equivalent (just a 'color' property)
export type InputIconPropertyValues = InputTextPropertyValues;

// Obtained from the square SDK source code
export const supportedFontFamilies = ['andale mono', 'arial', 'arial black', 'arial narrow', 'arial rounded mt bold', 'avant garde', 'baskerville', 'big caslon', 'bodoni mt', 'book antiqua', 'brush script mt', 'calibri', 'calisto mt', 'cambria', 'candara', 'century gothic', 'charcoal', 'comic sans ms', 'consolas', 'copperplate', 'copperplate gothic light', 'courier', 'courier new', 'cursive', 'didot', 'fantasy', 'franklin gothic medium', 'futura', 'garamond', 'geneva', 'georgia', 'gill sans', 'goudy old style', 'helvetica', 'helvetica neue', 'hoefler text', 'impact', 'lucida bright', 'lucida console', 'lucida grande', 'lucida sans unicode', 'lucida sans typewriter', 'monaco', 'monospace', 'optima', 'palatino', 'palatino linotype', 'papyrus', 'perpetua', 'rockwell', 'rockwell extra bold', 'sans-serif', 'segoe ui', 'serif', 'square market', 'square sans text', 'tahoma', 'times', 'times new roman', 'trebuchet ms', 'verdana'];

// Custom translations in order to find a more appropriate font that Square supports
// NOTE: Taken from Checkout.cshtml, but removed any that were redundant with automatic supported font detection (ex: cursive, Helvetica, etc.)
export const fontFamilyTranslation = {
	'Limelight': 'Impact',
	'Lora': 'Georgia',
	'Montserrat': 'Verdana',
	'Ubuntu': 'Trebuchet MS'
};

export class SquareCardOptions implements CardOptions {
	constructor(inputStyles: PaymentFormStyle) {
		const style: CardClassSelectors = {};
		style['.input-container'] = this.getContainerStyles(inputStyles);
		style['.input-container.is-error'] = this.getContainerErrorStyles(inputStyles);
		style['.input-container.is-focus'] = this.getContainerFocusStyles(inputStyles);
		style['input'] = this.getInputStyles(inputStyles);
		style['input.is-error'] = this.getInputErrorStyles(inputStyles);
		style['input::placeholder'] = this.getPlaceholderStyles(inputStyles);
		this.style = style;
	}

	/**
	 * Gets Square container styles (i.e. border) based on styles collected from the Cognito form
	 * @param inputStyles The input styles collected from the Cognito form
	 */
	private getContainerStyles(inputStyles: PaymentFormStyle): ComponentPropertyValues {
		const style: ComponentPropertyValues = {};
		// We don't need to pass styles for the outer container because we can just use old fashioned CSS on the iframe element.
		// To make sure the middle dividing border on a two-row layout is always there, these values are hard coded.
		style.borderColor = 'rgba(0,0,0,.15)';
		style.borderWidth = '0';
		return style;
	}

	/**
	 * Gets Square container styles (i.e. border) for error state based on styles collected from the Cognito form
	 * @param inputStyles The input styles collected from the Cognito form
	 */
	private getContainerErrorStyles(inputStyles: PaymentFormStyle): ComponentStatePropertyValues {
		const style: ComponentStatePropertyValues = {};
		style.borderColor = 'transparent';
		return style;
	}

	/**
	 * Gets Square container styles (i.e. border) for focus state based on styles collected from the Cognito form
	 * @param inputStyles The input styles collected from the Cognito form
	 */
	private getContainerFocusStyles(inputStyles: PaymentFormStyle): ComponentStatePropertyValues {
		const style: ComponentStatePropertyValues = {};
		style.borderColor = 'transparent';
		return style;
	}

	/**
	 * Gets Square input styles based on styles collected from the Cognito form
	 * @param inputStyles The input styles collected from the Cognito form
	 */
	private getInputStyles(inputStyles: PaymentFormStyle): InputPropertyValues {
		const style: InputPropertyValues = {};
		style.color = inputStyles.color || '#000';
		style.fontFamily = this.getFontFamily(inputStyles);
		style.backgroundColor = 'transparent';
		style.fontWeight = inputStyles.fontWeight || 'normal';
		style.fontSize = inputStyles.fontSize || '14px';
		return style;
	}

	/**
	 * Gets the closest matching font family that is supported by Square
	 * @param inputStyles The input styles collected from the Cognito form
	 */
	private getFontFamily(inputStyles: PaymentFormStyle) {
		let fontFamily = 'Arial';
		const origFontFamily = inputStyles.fontFamily || 'Arial';
		if (supportedFontFamilies.indexOf(origFontFamily.toLowerCase()) < 0) {
			fontFamily = 'Arial';
			let fontFamilyMatchIndex = -1;
			// Search supported fonts for a match
			supportedFontFamilies.forEach(supportedFont => {
				const supportedFontIndex = origFontFamily.toLowerCase().indexOf(supportedFont.toLowerCase());
				if (supportedFontIndex >= 0) {
					// Give priority to a matched font earlier in the original font string
					if (fontFamilyMatchIndex < 0 || supportedFontIndex < fontFamilyMatchIndex || (supportedFontIndex === fontFamilyMatchIndex && fontFamily.length < supportedFont.length)) {
						fontFamily = supportedFont;
						fontFamilyMatchIndex = supportedFontIndex;
					}
				}
			});
			// Search font translations for a match
			Object.keys(fontFamilyTranslation).forEach(function (fontKey) {
				const translatedFontIndex = origFontFamily.toLowerCase().indexOf(fontKey.toLowerCase());
				if (translatedFontIndex >= 0) {
					// Give priority to a matched font earlier in the original font string
					if (fontFamilyMatchIndex < 0 || translatedFontIndex < fontFamilyMatchIndex || (fontFamilyMatchIndex === translatedFontIndex && fontFamily.length < fontFamilyTranslation[fontKey].length)) {
						fontFamily = fontFamilyTranslation[fontKey];
						fontFamilyMatchIndex = translatedFontIndex;
					}
				}
			});
			if (fontFamilyMatchIndex < 0) {
				console.warn(`Font '${origFontFamily}' is not supported.`);
			}
		}
		else {
			fontFamily = origFontFamily;
		}
		return fontFamily;
	}

	/**
	 * Gets Square input styles for error state based on styles collected from the Cognito form
	 * @param inputStyles The input styles collected from the Cognito form
	 */
	private getInputErrorStyles(inputStyles: PaymentFormStyle): InputTextPropertyValues {
		const style: InputTextPropertyValues = {};
		style.color = inputStyles.color;
		return style;
	}

	/**
	 * Gets Square placeholder styles (i.e. color) based on styles collected from the Cognito form
	 * @param inputStyles The input styles collected from the Cognito form
	 */
	private getPlaceholderStyles(inputStyles: PaymentFormStyle): InputTextPropertyValues {
		const style: InputTextPropertyValues = {};
		style.color = inputStyles.placeholderColor || '#CCCCCC';
		return style;
	}

	style?: CardClassSelectors;
}
